import { useFetchUserProfilePicQuery } from 'api/api-users-v2';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

// Global flag to track if we've already made a successful request
let hasProfilePicBeenFetched = false;
let globalProfilePicData: any = null;

/**
 * Custom hook to manage profile picture with improved caching.
 * This ensures we only make one API call even across multiple components.
 */
export const useProfilePicture = (user: any) => {
  // Create a ref to track if this specific component instance has already tried to fetch
  const hasComponentFetchedRef = useRef(false);
  // Track if this component instance should skip the fetch
  const [shouldSkipFetch, setShouldSkipFetch] = useState(false);
  
  // Try to get the profile picture from the cache first
  const cachedProfilePic = useSelector((state: RootState) => {
    // Look for the profile pic data in the RTK Query cache
    const queries = state.users_v2?.queries || {};
    const cacheEntries = Object.values(queries);
    
    // Find a successful fetchUserProfilePic query
    const profilePicEntry = cacheEntries.find(
      (entry: any) => 
        entry?.endpointName === 'fetchUserProfilePic' && 
        entry?.status === 'fulfilled' &&
        entry?.data
    );
    
    if (profilePicEntry?.data) {
      // If we find data in the cache, update our global flag and data
      hasProfilePicBeenFetched = true;
      globalProfilePicData = profilePicEntry.data;
      
      // If this component hasn't decided to skip yet, do it now
      if (!shouldSkipFetch) {
        setShouldSkipFetch(true);
      }
    }
    
    return profilePicEntry?.data;
  });

  // Set initial skip state based on cached data and global flag
  useEffect(() => {
    if (!shouldSkipFetch && (hasProfilePicBeenFetched || Boolean(cachedProfilePic))) {
      setShouldSkipFetch(true);
    }
  }, [cachedProfilePic, shouldSkipFetch]);

  // Skip the API call if:
  // 1. We already have cached data from any previous component
  // 2. The global flag indicates we've already fetched successfully
  // 3. This component instance has already attempted a fetch
  // 4. There's no user data available
  // 5. This component instance has decided to skip
  const shouldSkip = Boolean(cachedProfilePic) || 
                     hasProfilePicBeenFetched || 
                     hasComponentFetchedRef.current || 
                     !user ||
                     shouldSkipFetch;
  
  // Use the RTK Query hook with our optimized skip condition
  const result = useFetchUserProfilePicQuery(undefined, {
    skip: shouldSkip,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  // Mark that this component instance has attempted a fetch
  if (!hasComponentFetchedRef.current && !shouldSkip) {
    hasComponentFetchedRef.current = true;
  }

  useEffect(() => {
    // If we have data from a successful fetch, update our global tracking flag
    if (result.data && !result.isLoading && !result.isFetching) {
      hasProfilePicBeenFetched = true;
      globalProfilePicData = result.data;
      setShouldSkipFetch(true);
    }
  }, [result.data, result.isLoading, result.isFetching]);

  // Return either the cached data or the fresh query result with proper typing
  return {
    ...result,
    // Ensure data always has the right shape with url property for TypeScript
    data: (cachedProfilePic || result.data || globalProfilePicData || {}) as { url?: string },
    isLoading: !cachedProfilePic && !globalProfilePicData && result.isLoading,
    isFetching: !cachedProfilePic && !globalProfilePicData && result.isFetching,
  };
}; 
